import { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingUp, Package, Clock3, Wallet, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { SectionCard } from '../../components/ui/SectionCard';
import api from '../../services/api';
import { PERIOD_OPTIONS, fmtAmount, fmtTons, fmtPct, CHART_PRIMARY } from './utils';
import { ProductRevenueChart } from './components/ProductRevenueChart';
import { DestinationMixChart } from './components/DestinationMixChart';
import { VolumeOtdChart } from './components/VolumeOtdChart';
import { TopClientsList } from './components/TopClientsList';

export default function ReportingPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !hasPermission('view_reporting')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, hasPermission, navigate]);

  const [period, setPeriod] = useState('all');
  const [overview, setOverview] = useState(null);
  const [byProduct, setByProduct] = useState([]);
  const [destinationMix, setDestinationMix] = useState([]);
  const [volumeOtdTrend, setVolumeOtdTrend] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ period });
    Promise.all([
      api.get(`/reporting/overview?${params}`),
      api.get(`/reporting/revenue-by-product?${params}`),
      api.get(`/reporting/destination-mix?${params}`),
      api.get(`/reporting/volume-otd-trend?${params}`),
      api.get(`/reporting/top-clients?${params}`),
    ])
      .then(([overviewRes, productRes, destRes, trendRes, clientsRes]) => {
        setOverview(overviewRes.data);
        setByProduct(productRes.data ?? []);
        setDestinationMix(destRes.data ?? []);
        setVolumeOtdTrend(trendRes.data ?? []);
        setTopClients(clientsRes.data ?? []);
      })
      .catch((err) => setError(err.message || t('common.error_occurred')))
      .finally(() => setLoading(false));
  }, [period, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const revenueTrend = useMemo(() => {
    const pct = overview?.revenue?.trendPct;
    if (pct == null) return { trend: undefined, label: undefined };
    return { trend: pct >= 0 ? 'up' : 'down', label: `${pct >= 0 ? '+' : ''}${pct}%` };
  }, [overview]);

  const volumeTrend = useMemo(() => {
    const pct = overview?.volume?.trendPct;
    if (pct == null) return { trend: undefined, label: undefined };
    return { trend: pct >= 0 ? 'up' : 'down', label: `${pct >= 0 ? '+' : ''}${pct}%` };
  }, [overview]);

  const otdTrend = useMemo(() => {
    const pct = overview?.otd?.trendPct;
    if (pct == null) return { trend: undefined, label: undefined };
    return { trend: pct >= 0 ? 'up' : 'down', label: `${pct >= 0 ? '+' : ''}${pct} pts` };
  }, [overview]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('reporting.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('reporting.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[12.5px] font-semibold"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>{t(`reporting.${opt.labelKey}`)}</option>
            ))}
          </select>
          <button className="h-9 px-3 rounded-md bg-primary text-white text-[12.5px] font-semibold flex items-center gap-1.5 hover:opacity-90 transition">
            <FileDown size={13} strokeWidth={1.75} />
            {t('reporting.btn_export')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
          <span>{error}</span>
          <button onClick={loadData} className="font-medium underline text-[12px] text-rose-700">
            {t('common.retry')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label={t('reporting.kpi_revenue')}
          value={loading ? '—' : fmtAmount(overview?.revenue?.amountEur, locale)}
          trend={revenueTrend.trend}
          trendLabel={revenueTrend.label}
          sparkline={overview?.revenue?.sparkline?.length > 1 ? overview.revenue.sparkline : undefined}
          sparklineColor={CHART_PRIMARY}
          icon={TrendingUp}
        />
        <KpiCard
          label={t('reporting.kpi_volume')}
          value={loading ? '—' : fmtTons(overview?.volume?.tons, locale)}
          trend={volumeTrend.trend}
          trendLabel={volumeTrend.label}
          sparkline={overview?.volume?.sparkline?.length > 1 ? overview.volume.sparkline : undefined}
          sparklineColor={CHART_PRIMARY}
          icon={Package}
        />
        <KpiCard
          label={t('reporting.kpi_otd')}
          value={loading ? '—' : fmtPct(overview?.otd?.pct)}
          trend={otdTrend.trend}
          trendLabel={otdTrend.label}
          icon={Clock3}
        />
        <KpiCard
          label={t('reporting.kpi_recovery')}
          value={loading ? '—' : fmtPct(overview?.recovery?.pct)}
          sub={t('reporting.kpi_recovery_sub')}
          icon={Wallet}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <SectionCard title={t('reporting.chart_revenue_by_product')}>
            <ProductRevenueChart data={byProduct} locale={locale} />
          </SectionCard>
        </div>
        <SectionCard title={t('reporting.chart_destination_mix')}>
          <DestinationMixChart data={destinationMix} />
        </SectionCard>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <SectionCard title={t('reporting.chart_volume_otd')}>
            <VolumeOtdChart data={volumeOtdTrend} locale={locale} />
          </SectionCard>
        </div>
        <SectionCard title={t('reporting.chart_top_clients')}>
          <TopClientsList data={topClients} locale={locale} />
        </SectionCard>
      </div>
    </div>
  );
}
